/* ──────────────────────────────────────────────────────────────────────────
 * Apollo Bearing Index — shared utilities (window.IFX)
 * assets/interchange-utils.js
 *
 * Loaded (deferred) before every other interchange-*.js file and by the
 * product-page mini widget. Exposes exactly one global: window.IFX.
 *
 *   IFX.escapeHtml / escapeAttr / debounce / formatMoney / showToast / track
 *   IFX.storage.get(key, fallback) / set(key, value) / remove(key)   (try/catch'd localStorage)
 *   IFX.motionOK()                       → false under prefers-reduced-motion
 *   IFX.define(name, value) / IFX.use(name) / IFX.ready(name) → Promise
 *   IFX.brandName(slug) / IFX.typeName(slug) / IFX.tierLabel(xrefType)
 *   IFX.fmt.mm(v, unit) / IFX.fmt.num(n)
 *
 * Vanilla ES2019, IIFE, idempotent (a second execution is a no-op).
 * ───────────────────────────────────────────────────────────────────────── */
(function () {
  "use strict";
  if (window.IFX && window.IFX.__v >= 2) return;

  var TOAST_DURATION_MS = 5000;
  var TOAST_LEAVE_MS = 260;
  var TOAST_MAX = 3;

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }
  var escapeAttr = escapeHtml;

  function debounce(fn, ms) {
    var timer = null;
    function debounced() {
      var self = this, args = arguments;
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () { timer = null; fn.apply(self, args); }, ms);
    }
    debounced.cancel = function () { if (timer) clearTimeout(timer); timer = null; };
    return debounced;
  }

  function formatMoney(cents) {
    var n = Number(cents);
    if (!Number.isFinite(n)) return "";
    try { return "$" + (n / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
    catch (err) { return "$" + (n / 100).toFixed(2); }
  }

  function getToastHost() {
    var host = document.querySelector(".ifx-toasts");
    if (host) return host;
    host = document.createElement("div");
    host.className = "ifx-toasts";
    host.setAttribute("role", "status");
    host.setAttribute("aria-live", "polite");
    (document.body || document.documentElement).appendChild(host);
    return host;
  }

  function showToast(message, opts) {
    try {
      opts = opts || {};
      var host = getToastHost();
      while (host.children.length >= TOAST_MAX) host.removeChild(host.firstElementChild);
      var tone = opts.tone === "err" ? "error" : "success";
      var toast = document.createElement("div");
      toast.className = "ifx-toast ifx-toast--" + tone;
      var msg = document.createElement("span");
      msg.className = "ifx-toast__msg";
      msg.textContent = String(message == null ? "" : message);
      toast.appendChild(msg);
      if (opts.linkHref) {
        var link = document.createElement("a");
        link.className = "ifx-toast__link";
        link.href = String(opts.linkHref);
        link.textContent = String(opts.linkLabel || "View");
        toast.appendChild(link);
      }
      var closeBtn = document.createElement("button");
      closeBtn.type = "button";
      closeBtn.className = "ifx-toast__close";
      closeBtn.setAttribute("aria-label", "Dismiss notification");
      closeBtn.innerHTML = "&times;";
      toast.appendChild(closeBtn);
      var dismissed = false, autoTimer = null;
      function dismiss() {
        if (dismissed) return;
        dismissed = true;
        if (autoTimer) clearTimeout(autoTimer);
        toast.classList.add("is-leaving");
        setTimeout(function () { if (toast.parentNode) toast.parentNode.removeChild(toast); }, TOAST_LEAVE_MS);
      }
      closeBtn.addEventListener("click", dismiss);
      autoTimer = setTimeout(dismiss, opts.duration || TOAST_DURATION_MS);
      host.appendChild(toast);
      return toast;
    } catch (err) {
      try { console.error("[IFX] showToast failed", err); } catch (ignore) { /* noop */ }
      return null;
    }
  }

  function track(name, payload) {
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, payload || {})); } catch (err) { /* never throws */ }
    try { document.dispatchEvent(new CustomEvent(name, { detail: payload || {} })); } catch (err) { /* never throws */ }
  }

  /* ── storage (never throws; private mode / blocked storage → fallback) ── */
  var PREFIX = "ifx:";
  var storage = {
    get: function (key, fallback) {
      try {
        var raw = window.localStorage.getItem(PREFIX + key);
        return raw == null ? fallback : JSON.parse(raw);
      } catch (err) { return fallback; }
    },
    set: function (key, value) {
      try { window.localStorage.setItem(PREFIX + key, JSON.stringify(value)); return true; } catch (err) { return false; }
    },
    remove: function (key) {
      try { window.localStorage.removeItem(PREFIX + key); } catch (err) { /* noop */ }
    },
  };

  function motionOK() {
    try { return !window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (err) { return true; }
  }

  /* ── tiny module registry (lazy view scripts register when they load) ── */
  var modules = {};
  var waiters = {};
  function define(name, value) {
    modules[name] = value;
    (waiters[name] || []).forEach(function (resolve) { resolve(value); });
    waiters[name] = [];
    return value;
  }
  function use(name) { return modules[name]; }
  function ready(name) {
    if (modules[name]) return Promise.resolve(modules[name]);
    return new Promise(function (resolve) { (waiters[name] = waiters[name] || []).push(resolve); });
  }

  /* ── display names ──────────────────────────────────────────────────── */
  var BRAND_NAMES = {
    skf: "SKF", fag: "FAG", ina: "INA", schaeffler: "Schaeffler", nsk: "NSK", ntn: "NTN", timken: "Timken",
    koyo: "Koyo", jtekt: "JTEKT", nachi: "NACHI", rhp: "RHP", mrc: "MRC", fafnir: "Fafnir", torrington: "Torrington",
    iko: "IKO", mcgill: "McGill", rbc: "RBC", dodge: "Dodge", sealmaster: "Sealmaster", browning: "Browning",
    link_belt: "Link-Belt", rexnord: "Rexnord", snr: "SNR", consolidated: "Consolidated", steyr: "Steyr",
    kaydon: "Kaydon", rollway: "Rollway", hyatt: "Hyatt", new_departure: "New Departure", ndh: "NDH",
    norma_hoffman: "Norma-Hoffman", riv: "RIV", bower: "Bower", bca: "BCA", federal: "Federal", iptci: "IPTCI",
    asahi: "Asahi", ami: "AMI", fyh: "FYH", peer: "PEER", nke: "NKE", zkl: "ZKL", cooper: "Cooper", thk: "THK",
    american_roller: "American Roller", barden: "Barden", nice: "Nice", "ntn-bower": "NTN-Bower", ntn_bower: "NTN-Bower",
    nadella: "Nadella", hoover: "Hoover", jefrey: "Jeffrey", abc: "ABC", general: "General", urb: "URB", zwz: "ZWZ",
    hrb: "HRB", lyc: "LYC", nrb: "NRB", tsubaki: "Tsubaki", boca: "Boca", cr_seals: "CR Seals", ggb: "GGB",
  };
  function brandName(slug) {
    var s = String(slug || "").toLowerCase();
    if (BRAND_NAMES[s]) return BRAND_NAMES[s];
    if (!s) return "";
    return s.length <= 4 ? s.toUpperCase() : s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ");
  }

  var TYPE_NAMES = {
    deep_groove_ball: "deep groove ball", angular_contact_ball: "angular contact ball", self_aligning_ball: "self-aligning ball",
    cylindrical_roller: "cylindrical roller", spherical_roller: "spherical roller", tapered_roller: "tapered roller",
    needle_roller: "needle roller", thrust_ball: "thrust ball", thrust_roller: "thrust roller", insert_bearing: "insert bearing",
    pillow_block: "pillow block unit", flange_mounted: "flange unit", take_up: "take-up unit", cartridge: "cartridge unit",
    spherical_plain: "spherical plain", rod_end: "rod end", wheel_hub_unit: "wheel hub unit", cam_follower: "cam follower",
    linear_block: "linear block", linear_bushing: "linear bushing", four_point_contact_ball: "four-point contact ball",
    cylindrical_roller_thrust: "cylindrical roller thrust", spherical_roller_thrust: "spherical roller thrust",
  };
  function typeName(slug) {
    if (!slug) return "";
    return TYPE_NAMES[slug] || String(slug).replace(/_/g, " ");
  }

  var TIER_LABELS = {
    family: "Same family", family_variant: "Family variant", verified: "Verified interchange",
    iso_equivalent: "ISO-equivalent", evidence: "Catalog cross-ref", evidence_unverified: "Cross-ref, unverified",
  };
  function tierLabel(xrefType) {
    return TIER_LABELS[xrefType] || (xrefType ? String(xrefType).replace(/_/g, " ") : "Same family");
  }

  var MM_PER_INCH = 25.4;
  var fmt = {
    num: function (n) {
      var v = Number(n);
      if (!Number.isFinite(v)) return "";
      try { return v.toLocaleString("en-US"); } catch (err) { return String(v); }
    },
    mm: function (mm, unit) {
      if (mm == null || !Number.isFinite(Number(mm))) return "–";
      var v = Number(mm);
      if (unit === "in") return (Math.round((v / MM_PER_INCH) * 10000) / 10000).toFixed(4).replace(/0+$/, "").replace(/\.$/, "") + "″";
      return (Math.round(v * 1000) / 1000) + " mm";
    },
    dims: function (b, unit) {
      if (b.boreMm == null && b.odMm == null && b.widthMm == null) return "";
      return fmt.mm(b.boreMm, unit) + " × " + fmt.mm(b.odMm, unit) + " × " + fmt.mm(b.widthMm, unit);
    },
  };

  window.IFX = {
    __v: 2,
    escapeHtml: escapeHtml,
    escapeAttr: escapeAttr,
    debounce: debounce,
    formatMoney: formatMoney,
    showToast: showToast,
    track: track,
    storage: storage,
    motionOK: motionOK,
    define: define,
    use: use,
    ready: ready,
    brandName: brandName,
    typeName: typeName,
    tierLabel: tierLabel,
    fmt: fmt,
    views: {},
  };
})();
